import React, { useContext } from 'react';
import { EditTool } from '../EditTool';
import { insertDraggableElement } from '../DraggableLabel/ContextDraggableLabel';
import {
  TextPresentationPageContext,
  ActionTypes as TextPresentActiontypes,
} from '../../contexts/textPresentationPageContext';
import { WordsPageContext } from '../../contexts/wordsPageContext';
import { StyledWordContext } from '../../contexts/styledWordContext';
import { EditToolContext } from '../../contexts/editToolContext';
import { allAreEqual, getCollidedLabel, getStageColors } from '../../utils';
import { useMainContentSize } from '../../hooks/useMainContentSize';
import { ExemplarCombined, ILabel } from '../../types';
import { WORDS, emergentModes, laterModes } from '../../constants/navigation';

interface IEditToolContainer {
  mode: string;
  onClose: () => void;
  isFullScreen: boolean;
  exemplar: ExemplarCombined;
  exemplarId: number;
  exemplarType?: string;
  stage: string;
}

export const EditToolContainer: React.FC<IEditToolContainer> = (props): JSX.Element => {
  const { mode, onClose, isFullScreen, stage, exemplar, exemplarId, exemplarType } = props;

  const { state: textPresentationPageState, dispatch: textPresentationPageDispatch } =
    useContext(TextPresentationPageContext);
  const {
    state: wordsPageState,
    setLabels,
    saveLabelsUpdates,
    saveHideVisibleLabelUpdates,
    saveShowHiddenLabelUpdates,
    saveHideLabelsUpdates,
  } = useContext(WordsPageContext);
  const { labels } = wordsPageState;
  const { handleHighlightingTool, handleColorPick, handleHideHighlightsTool, handleHideText, handleImageTool } =
    useContext(StyledWordContext);
  const { isEditMode } = useContext(EditToolContext);
  const [x, y] = useMainContentSize();

  const isAnyLabelShown = !!labels.find(label => {
    return label.isManuallyHidden && !label.isHidden;
  });

  const { main: panelBgColor, dark: toolBgColor, light: lightColor } = getStageColors(stage);

  const handleCloseEditTool = () => {
    handleImageTool(false);
    onClose();
  };

  const handleAddLabel = () => {
    return insertDraggableElement(labels, setLabels, saveLabelsUpdates, '', true);
  };

  const handleAllLabelsClick = (checked: boolean) => {
    const manuallyHiddenLabelsState: boolean[] = [];
    const payload = labels.map(label => {
      !label.isHidden && manuallyHiddenLabelsState.push(label.isManuallyHidden);
      const isVisible = !(label.position && getCollidedLabel(label.position, wordsPageState.images, { x, y }));

      return isVisible ? { ...label, isManuallyHidden: !checked } : label;
    });
    setLabels(payload);
    allAreEqual(manuallyHiddenLabelsState)
      ? saveHideLabelsUpdates(labels, payload)
      : saveLabelsUpdates(labels, payload);
  };

  const handleLabelClick = (id: number) => {
    let isLabelWasHidden = false;
    const state = labels.map((label: ILabel) => {
      if (id === label.id) {
        isLabelWasHidden = !label.isManuallyHidden === label.originalIsManuallyHidden;

        return {
          ...label,
          isManuallyHidden: !label.isManuallyHidden,
          originalIsManuallyHidden:
            'originalIsManuallyHidden' in label ? label.originalIsManuallyHidden : label.isManuallyHidden,
        };
      }

      return label;
    });

    const prevState = labels.map(label => ({ ...label, isHighlighted: false }));
    setLabels(state);
    isLabelWasHidden ? saveShowHiddenLabelUpdates(prevState, state) : saveHideVisibleLabelUpdates(prevState, state);
  };

  const handleLabelOn = (id: number) => {
    const payload = labels.map(label => {
      return { ...label, isHighlighted: id === label.id };
    });
    setLabels(payload);
  };

  const handleLabelOut = () => {
    const payload = labels.map(label => {
      return { ...label, isHighlighted: false };
    });
    setLabels(payload);
  };

  const handleAllLabelsOn = () => {
    const payload = labels.map(label => {
      return { ...label, isHighlighted: true };
    });
    setLabels(payload);
  };

  const handleNotesVisibility = () => {
    const payload = textPresentationPageState.notes.map(note => {
      return { ...note, isManuallyHidden: !note.isManuallyHidden };
    });

    textPresentationPageDispatch({ type: TextPresentActiontypes.UPDATE_NOTE, payload });
  };

  const getWordsPageProps = () => ({
    allLabelsClick: handleAllLabelsClick,
    labelClick: handleLabelClick,
    addLabel: handleAddLabel,
    labels: labels,
    isAnyLabelShown: isAnyLabelShown,
    onLabelOn: handleLabelOn,
    onLabelOut: handleLabelOut,
    onAllLabels: handleAllLabelsOn,
  });

  const getSentencesPageProps = () => ({
    handleHideHighlights: handleHideHighlightsTool,
    handleColorPick: handleColorPick,
    handleHighlightingTool: handleHighlightingTool,
    handleHideText: handleHideText,
  });

  if (!isEditMode) {
    return <></>;
  }

  return (
    <>
      {emergentModes.includes(mode) && (
        <EditTool
          mode={mode}
          {...(mode === WORDS ? getWordsPageProps() : {})}
          {...(mode !== WORDS ? getSentencesPageProps() : {})}
          panelBgColor={panelBgColor}
          toolBgColor={toolBgColor}
          lightColor={lightColor}
          onClose={handleCloseEditTool}
          isFullScreen={isFullScreen}
          exemplar={exemplar}
          exemplarId={exemplarId}
          exemplarType={exemplarType}
          stage={stage}
        />
      )}
      {laterModes.includes(mode) && (
        <EditTool
          handleHideHighlights={handleHideHighlightsTool}
          isNotesExist={textPresentationPageState.notes.length > 0}
          handleNotesVisibility={handleNotesVisibility}
          handleColorPick={handleColorPick}
          handleHighlightingTool={handleHighlightingTool}
          handleHideText={handleHideText}
          mode={mode}
          panelBgColor={panelBgColor}
          toolBgColor={toolBgColor}
          lightColor={lightColor}
          onClose={handleCloseEditTool}
          isFullScreen={isFullScreen}
          exemplar={exemplar}
          exemplarId={exemplarId}
          exemplarType={exemplarType}
          stage={stage}
        />
      )}
    </>
  );
};
